import { AffiliatePartnerCard } from './AffiliatePartnerCard';
import { useAffiliatePartners } from '@/hooks/useAffiliatePartners';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

export const AffiliatePartnerGrid = () => {
  const { data: partners, isLoading, error } = useAffiliatePartners();

  if (isLoading) {
    return (
      <div className="space-y-12">
        {[1, 2].map((section) => (
          <div key={section}>
            <Skeleton className="h-8 w-48 mb-6" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((card) => (
                <Skeleton key={card} className="h-64" />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load travel partners. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  if (!partners || partners.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No travel partners available at the moment. Check back soon!
        </AlertDescription>
      </Alert>
    );
  }

  // Group partners by category
  const groupedPartners = partners.reduce((acc, partner) => {
    if (!acc[partner.category]) {
      acc[partner.category] = [];
    }
    acc[partner.category].push(partner);
    return acc;
  }, {} as Record<string, typeof partners>);

  return (
    <div className="space-y-12">
      {Object.entries(groupedPartners).map(([category, categoryPartners]) => (
        <section key={category}>
          <h2 className="text-2xl font-bold mb-6">{category}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categoryPartners.map((partner) => (
              <AffiliatePartnerCard key={partner.id} partner={partner} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};
